/** Human-readable message for table/API errors; avoids "[object Object]" and raw JSON in the UI. */

const TABLE_ERROR_TYPE_MESSAGES: Record<string, string> = {
  TABLE_FETCH_TIMEOUT:
    'This collection took too long to load. Check your connection and try again.',
  TABLE_META_INVALID:
    'Collection metadata could not be loaded. Try refreshing the page.',
  ITEM_UPDATE_ERROR: 'Could not save your changes. Try again or refresh the page.',
  RELATION_FIELD_ERROR:
    'A related collection could not be loaded. You can keep working; some links may be incomplete.',
  COLLECTION_NOT_FOUND_404: 'Collection not found.',
  COMPONENT_NOT_FOUND: 'Collection not found or you do not have access.',
  COLLECTION_SLUG_AMBIGUOUS:
    'Multiple collections match this name. Open it from the menu with the correct workspace selected.',
}

/** Keys that are not meaningful to show as JSON when there is no user message. */
const INTERNAL_TABLE_ERROR_KEYS = new Set([
  'tableKey',
  'slug',
  'message',
  'type',
  'status',
  'stack',
  'name',
  'code',
  'errno',
  'componentMeta',
  'collection',
  'isMongoCollection',
  'error',
  'statusText',
  'availableSlugs',
  'originalSlug',
])

function hasOnlyInternalKeys(o: Record<string, unknown>): boolean {
  const keys = Object.keys(o).filter((k) => o[k] != null && o[k] !== '')
  if (keys.length === 0) return true
  return keys.every((k) => INTERNAL_TABLE_ERROR_KEYS.has(k))
}

function collectionLabelFromPayload(o: Record<string, unknown>): string | undefined {
  const s = o.slug
  const t = o.tableKey
  if (typeof s === 'string' && s.trim()) return s.trim()
  if (typeof t === 'string' && t.trim()) return t.trim()
  return undefined
}

/** Safe snapshot for logs (avoids "[object Object]" and handles Error / circular data). */
export function serializeErrorForDiagnostics(err: unknown): Record<string, unknown> {
  if (err == null) {
    return { kind: 'nullish', value: err }
  }
  if (typeof err === 'string') {
    return { kind: 'string', message: err }
  }
  if (err instanceof Error) {
    return {
      kind: 'Error',
      name: err.name,
      message: err.message,
      stack: err.stack,
    }
  }
  if (typeof err !== 'object') {
    return { kind: typeof err, value: String(err) }
  }
  const o = err as Record<string, unknown>
  try {
    return { kind: 'object', payload: JSON.parse(JSON.stringify(o)) as Record<string, unknown> }
  } catch {
    const keys = Object.keys(o)
    const snapshot: Record<string, string> = {}
    for (const k of keys) {
      const v = o[k]
      if (v instanceof Error) {
        snapshot[k] = `${v.name}: ${v.message}`
      } else if (typeof v === 'object' && v !== null) {
        snapshot[k] = '[object]'
      } else {
        snapshot[k] = String(v)
      }
    }
    return { kind: 'object-non-json', keys, snapshot }
  }
}

function logGenericUserFacingError(err: unknown, reason: string): void {
  console.error(
    `[formatErrorForDisplay] Showing generic user message (${reason}). Diagnostics:`,
    serializeErrorForDiagnostics(err)
  )
}

export function formatErrorForDisplay(err: unknown): string {
  if (err == null || err === '') {
    return 'Failed to load data'
  }
  if (typeof err === 'string') {
    return err
  }
  if (typeof err !== 'object') {
    return String(err)
  }
  const o = err as Record<string, unknown>

  const typeStr = typeof o.type === 'string' ? o.type.trim() : ''
  if (typeStr) {
    const mapped = TABLE_ERROR_TYPE_MESSAGES[typeStr]
    const msg = typeof o.message === 'string' ? o.message.trim() : ''
    if (mapped) {
      return msg && msg !== mapped ? `${mapped} ${msg}` : mapped
    }
    if (msg) {
      return msg
    }
    logGenericUserFacingError(o, `unmapped type "${typeStr}" and no message`)
    return 'Something went wrong. Try again or refresh the page.'
  }

  if (typeof o.message === 'string' && o.message.length > 0) {
    return o.message
  }
  if (o.message != null) {
    try {
      return JSON.stringify(o.message)
    } catch {
      return String(o.message)
    }
  }

  if (hasOnlyInternalKeys(o)) {
    const label = collectionLabelFromPayload(o)
    if (label) {
      console.warn(
        '[formatErrorForDisplay] Incomplete error payload (no message/type); using collection label.',
        serializeErrorForDiagnostics(o)
      )
      return `Could not load "${label}". Try again or refresh the page.`
    }
    logGenericUserFacingError(o, 'only internal / non-display keys')
    return 'Something went wrong. Try again or refresh the page.'
  }

  try {
    return JSON.stringify(o)
  } catch {
    return 'Failed to load data'
  }
}
